import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput, formatPhoneForSupabase, isValidUSPhone } from '@/components/PhoneInput';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { AuthLayout } from '@/components/AuthLayout';
import { logActivity } from '@/lib/activityLogger';

// Helper to validate redirect URLs for security
const getValidRedirect = (raw: string | null): string | null => {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    if (url.protocol !== 'https:') return null;
    if (!url.hostname.endsWith('.bluelux.com')) return null;
    return url.toString();
  } catch {
    return null;
  }
};

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Read and validate redirect parameter
  const searchParams = new URLSearchParams(location.search);
  const validRedirectUrl = getValidRedirect(searchParams.get('redirect'));
  
  // Email/Password form
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Phone OTP form
  const [phone, setPhone] = useState('');

  // Rate limiting helper functions
  const checkRateLimit = (identifier: string): boolean => {
    const attemptKey = `login_attempts_${identifier}`;
    const stored = localStorage.getItem(attemptKey);
    
    if (!stored) return false;
    
    try {
      const attempts = JSON.parse(stored);
      const fifteenMinutes = 15 * 60 * 1000; // 900000ms
      
      // Check if more than 5 attempts in last 15 minutes
      if (attempts.count >= 5 && Date.now() - attempts.time < fifteenMinutes) {
        return true; // Rate limit exceeded
      }
      
      // Clean up old entries
      if (Date.now() - attempts.time > fifteenMinutes) {
        localStorage.removeItem(attemptKey);
      }
    } catch (e) {
      // Invalid data, clean it up
      localStorage.removeItem(attemptKey);
    }
    
    return false;
  };

  const recordFailedAttempt = (identifier: string) => {
    const attemptKey = `login_attempts_${identifier}`;
    const stored = localStorage.getItem(attemptKey);
    
    let attempts = { count: 0, time: Date.now() };
    
    if (stored) {
      try {
        attempts = JSON.parse(stored);
        attempts.count += 1;
        attempts.time = Date.now();
      } catch (e) {
        // Invalid data, start fresh
        attempts = { count: 1, time: Date.now() };
      }
    } else {
      attempts = { count: 1, time: Date.now() };
    }
    
    localStorage.setItem(attemptKey, JSON.stringify(attempts));
  };

  const clearAttempts = (identifier: string) => {
    const attemptKey = `login_attempts_${identifier}`;
    localStorage.removeItem(attemptKey);
  };

  // Redirect authenticated users - simplified with cookie domain sharing
  useEffect(() => {
    if (user) {
      const target = validRedirectUrl || '/';
      if (target.startsWith('http')) {
        window.location.href = target; // Cross-subdomain redirect
      } else {
        navigate(target, { replace: true });
      }
    }
  }, [user, navigate, validRedirectUrl]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Check rate limit and show warning (doesn't block)
      if (checkRateLimit(email)) {
        toast.warning('Multiple failed login attempts detected. Please reset your password if you\'re having trouble.', {
          duration: 6000
        });
      }
      
      // Auto-detect based on password field
      if (password.trim()) {
        // Email + Password login
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;

        // Log successful login
        if (data.user) {
          clearAttempts(email); // Clear rate limit tracking
          await logActivity({
            actionType: 'login_success',
            actionCategory: 'auth',
            description: 'User logged in successfully via email',
            userId: data.user.id,
          });
        }

        toast.success('Welcome back!');
        // Cookie is automatically set and shared across *.bluelux.com
        const target = validRedirectUrl || '/';
        if (target.startsWith('http')) {
          window.location.href = target;
        } else {
          navigate(target, { replace: true });
        }
      } else {
        // Email + Magic Link
        const { error } = await supabase.auth.signInWithOtp({
          email,
          options: {
            shouldCreateUser: false,
          }
        });

        if (error) throw error;

        toast.success('Check your email for the magic link!');
        navigate('/auth/verify', { 
          state: { 
            email, 
            type: 'email',
            redirectUrl: validRedirectUrl,
          } 
        });
      }
    } catch (error: any) {
      recordFailedAttempt(email); // Track failed attempt
      toast.error(error.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValidUSPhone(phone)) {
      toast.error('Please enter a valid US phone number');
      return;
    }

    setLoading(true);
    
    const formattedPhone = formatPhoneForSupabase(phone);

    try {
      // Check rate limit and show warning (doesn't block)
      if (checkRateLimit(formattedPhone)) {
        toast.warning('Multiple failed login attempts detected. Please reset your password if you\'re having trouble.', {
          duration: 6000
        });
      }
      
      // Auto-detect based on password field
      if (password.trim()) {
        // Phone + Password login
        const { data, error } = await supabase.auth.signInWithPassword({
          phone: formattedPhone,
          password,
        });

        if (error) throw error;

        // Log successful login
        if (data.user) {
          clearAttempts(formattedPhone); // Clear rate limit tracking
          await logActivity({
            actionType: 'login_success',
            actionCategory: 'auth',
            description: 'User logged in successfully via phone',
            userId: data.user.id,
          });
        }

        toast.success('Welcome back!');
        // Cookie is automatically set and shared across *.bluelux.com
        const target = validRedirectUrl || '/';
        if (target.startsWith('http')) {
          window.location.href = target;
        } else {
          navigate(target, { replace: true });
        }
      } else {
        // Phone + OTP
        const { error } = await supabase.auth.signInWithOtp({
          phone: formattedPhone,
          options: { shouldCreateUser: false }
        });

        if (error) throw error;

        toast.success('OTP sent to your phone!');
        navigate('/auth/verify', { 
          state: { 
            phone: formattedPhone, 
            type: 'phone',
            redirectUrl: validRedirectUrl,
          } 
        });
      }
    } catch (error: any) {
      recordFailedAttempt(formattedPhone); // Track failed attempt
      toast.error(error.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="w-full max-w-md">
        <div className="bg-card border border-border rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">Welcome Back</h1>
            <p className="text-muted-foreground">Sign in to access the BlueLux Team Portal</p>
          </div>

          <Tabs defaultValue="email" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="email">Email</TabsTrigger>
              <TabsTrigger value="phone">Phone</TabsTrigger>
            </TabsList>

            <TabsContent value="email">
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@team.bluelux.com"
                    required
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label htmlFor="password">
                      Password <span className="text-xs font-normal text-muted-foreground">(optional)</span>
                    </Label>
                    <Link 
                      to="/auth/reset-password" 
                      className="text-xs text-primary hover:underline"
                      tabIndex={-1}
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Leave empty for magic link"
                    className="bg-muted/30 border-muted-foreground/20 text-[hsl(220,40%,45%)]"
                  />
                  <p className="text-xs text-muted-foreground mt-1.5">
                    Enter your password or leave empty to receive a magic link via email
                  </p>
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Processing...' : 'Continue'}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="phone">
              <form onSubmit={handlePhoneLogin} className="space-y-4">
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <PhoneInput
                    id="phone"
                    value={phone}
                    onChange={setPhone}
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <Label htmlFor="phone-password-input">
                      Password <span className="text-xs font-normal text-muted-foreground">(optional)</span>
                    </Label>
                    <Link 
                      to="/auth/reset-password" 
                      className="text-xs text-primary hover:underline"
                      tabIndex={-1}
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <Input
                    id="phone-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Leave empty for OTP code"
                    className="bg-muted/30 border-muted-foreground/20 text-[hsl(220,40%,45%)]"
                  />
                  <p className="text-xs text-muted-foreground mt-1.5">
                    Enter your password or leave empty to receive a one-time code via SMS
                  </p>
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Processing...' : 'Continue'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            Don't have an account?{' '}
            <Link to="/auth/signup" className="text-primary hover:underline font-medium">
              Sign up
            </Link>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
